import React from 'react'
import { PaletteColor, useTheme } from '@mui/material'
import { Extension, ExtensionCategory, ExtensionCategoryNames, ExtensionState, ExtensionTypes } from '../../types'
import { Ai360PowerFixLogo, Ai360PowerShareLogo, EonLogo, InexogyLogo, LuoxEnergySvg, OStromSvg, TibberSvg } from '../../resources'
import useModals from 'src/Hooks/useModals'
import { useParams } from 'react-router-dom'
import { getContractStates, getExtensionVisibility, getHasActiveTariff } from '../../utils'
import useInstallationExtensionData from './use-installation-extension-data'
import { useExtensionVisibility } from './use-extension-visibility'
import { extensionColors } from '../../colors'
import { isFlowTheme } from 'src/Libs/utils'

interface ReturnType {
  extensions: Extension[]
  hasAtLeastOneContractState: boolean
}

/**
 * This hook provides the installation extensions. It includes information about the extensions, their visibility, and their contract states.
 */
const useInstallationExtensions = (): ReturnType => {
  const theme = useTheme()
  const {
    openInexogyModal,
    openLuoxDirectMarketingModal,
    openLuoxDynamicTariffModal,
    openOStromModal,
    openTibberModal,
    openEonModal,
    openAi360PowerShareModal,
    openAi360PowerFixModal,
  } = useModals()
  const { installationId } = useParams<{ installationId: string }>()
  const { installationExtensionsState, isLoading, loadInstallationExtensionStates } = useInstallationExtensionData(installationId)
  const contractStates = getContractStates(installationExtensionsState)
  const {
    luoxDirectMarketingState,
    luoxDynamicTariffState,
    ostromState,
    inexogyState,
    tibberState,
    ai360PowerShareState,
    ai360PowerFixState,
    hasAtLeastOneContractState,
  } = contractStates
  const extensionVisibility = getExtensionVisibility(useExtensionVisibility(), isFlowTheme)
  const hasActiveTariff = getHasActiveTariff(contractStates)

  const ExtensionCategories: ExtensionCategory[] = [
    {
      name: ExtensionCategoryNames.DirectMarketing,
      paletteColor: theme.palette.olive,
      variant: 'light',
    },
    {
      name: ExtensionCategoryNames.DynamicEnergyTariff,
      paletteColor: theme.palette.secondary,
      variant: 'light',
    },
    {
      name: ExtensionCategoryNames.SmartMeter,
      paletteColor: theme.palette.warning,
      variant: 'light',
    },
    {
      name: ExtensionCategoryNames.ElectricityTariffAddOn,
      paletteColor: theme.palette.blue as PaletteColor,
      variant: 'light',
    },
    {
      name: ExtensionCategoryNames.ProsumerTariff,
      paletteColor: theme.palette.primary,
      variant: 'light',
    },
  ]

  /**
   * Note: There is a backend ticket to add all extensions which should be displayed to the "extension" route. At that point the visibility logic can be refactored to only display extensions part of that route.
   */
  const extensions: Record<ExtensionTypes, Extension> = {
    luoxDirectMarketing: {
      name: 'LuoxDirectMarketing',
      category: ExtensionCategories[0],
      logo: <LuoxEnergySvg />,
      contractState: luoxDirectMarketingState,
      onClick: () => openLuoxDirectMarketingModal({ extensionState: luoxDirectMarketingState, isLoading }),
      isVisible: extensionVisibility.luoxDirectMarketing,
      isAvailable: true,
      isLoading,
    },
    luox: {
      name: 'Luox',
      category: ExtensionCategories[1],
      logo: <LuoxEnergySvg />,
      contractState: luoxDynamicTariffState,
      onClick: () => openLuoxDynamicTariffModal({ extensionState: luoxDynamicTariffState, hasActiveTariff, isLoading }),
      isVisible: extensionVisibility.luox,
      isAvailable: true,
      isLoading,
    },
    ostrom: {
      name: 'Ostrom',
      category: ExtensionCategories[1],
      logo: <OStromSvg textFill={extensionColors.ostrom.primary} />,
      contractState: ostromState,
      onClick: () => openOStromModal({ extensionState: ostromState, hasActiveTariff, loadInstallationExtensionStates, isLoading }),
      isVisible: extensionVisibility.ostrom,
      isAvailable: true,
      isLoading,
    },
    tibber: {
      name: 'Tibber',
      category: ExtensionCategories[1],
      logo: <TibberSvg fill={extensionColors.tibber.primary} textFill={extensionColors.tibber.primary} />,
      contractState: tibberState,
      onClick: () => openTibberModal({ extensionState: tibberState, hasActiveTariff, loadInstallationExtensionStates, isLoading }),
      isVisible: extensionVisibility.tibber,
      isAvailable: true,
      isLoading,
    },
    inexogy: {
      name: 'Inexogy',
      category: ExtensionCategories[2],
      logo: <InexogyLogo />,
      contractState: inexogyState,
      onClick: () => openInexogyModal({ extensionState: inexogyState, isLoading }),
      isVisible: extensionVisibility.inexogy,
      isAvailable: true,
      isLoading,
    },
    eon: {
      name: 'E.ON',
      category: ExtensionCategories[3],
      logo: <EonLogo />,
      contractState: undefined, // BE does not provide contract state for E.ON extension
      onClick: () => openEonModal(),
      isVisible: extensionVisibility.eon,
      isAvailable: true,
      isLoading,
    },
    /* ai360PowerShare and ai360PowerFix can be ordered regardless of any other active tariff, so they're never blocked by hasActiveTariff */
    ai360PowerShare: {
      name: 'ai360 power share',
      category: ExtensionCategories[4],
      logo: <Ai360PowerShareLogo />,
      contractState: ai360PowerShareState,
      onClick: () => openAi360PowerShareModal({ extensionState: ai360PowerShareState, hasActiveTariff: false, isLoading }),
      isVisible: extensionVisibility.ai360PowerShare,
      isAvailable: false,
      isLoading,
    },
    ai360PowerFix: {
      name: 'ai360 power fix',
      category: ExtensionCategories[4],
      logo: <Ai360PowerFixLogo />,
      contractState: ai360PowerFixState,
      onClick: () => openAi360PowerFixModal({ extensionState: ai360PowerFixState, hasActiveTariff: false, isLoading }),
      isVisible: extensionVisibility.ai360PowerFix,
      isAvailable: true,
      isLoading,
    },
  }

  const sortedExtensions = Object.values(extensions).sort((a, b) => {
    const getPriority = (state?: ExtensionState): number => {
      if (state === 'active') return 0
      if (state === 'pending') return 1
      return 2
    }
    const priorityDiff = getPriority(a.contractState) - getPriority(b.contractState)

    if (priorityDiff !== 0) return priorityDiff
    return a.category.name.localeCompare(b.category.name)
  })

  return { extensions: sortedExtensions, hasAtLeastOneContractState }
}

export default useInstallationExtensions
